import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Spin,
  Tooltip,
} from "antd";
import { getProtocolNumber } from "../../../services/get-protocol-number";
import { getAllSupplierOrg } from "../../../services/DataTransferAPI/get-all-supplier-org";
import { getDetails } from "../../../services/get-details";
import { getCurrentDTSVersion } from "../../../services/DataTransferAPI/get-current-dts-version";
import { DTSTransferMessage, validateDTSVersion } from "../../DataTransfer/Details/utils";
import TransferContacts from "../../DataTransfer/Contact/transfer-contacts";
import { SaveDraft } from "../../../services/save-draft";
import { getUserEmail } from "../../../services/utils";

type ProtocolData = {
  studyName: string;
  studyNumber: string;
};

type OfflineDtsDialogProps = {
  isModalOpen: boolean;
  setIsOfflineDtsModalOpen: (isOpen: boolean) => void;
  openNotification: (type: NotificationType, description: string) => void;
  onCreateSuccess: () => void;
};

type NotificationType = "success" | "info" | "warning" | "error";

const OfflineDtsDailog: React.FC<OfflineDtsDialogProps> = ({ isModalOpen, setIsOfflineDtsModalOpen, openNotification, onCreateSuccess }) => {
    const [formRef] = Form.useForm();
    const [msdDataLoading, setMsdDataLoading] = useState(false);
    const [supplierLoading, setSupplierLoading] = useState(false);
    const [MSDCompoundODMOptions, setMSDCompoundODMOptions] = useState<any>([]);
    const [supplierOptionsValue, setSupplierOptionsValue] = useState<any[]>([]);
    const [transferIdList, setTransferIdList] = useState<any[]>([]);
    const [disableDTS, setDisableDTS] = useState(true);
    const [activeDTSVersion, setActiveDTSVersion] = useState<number>(0);
    const [activeDTSVersionStatus, setActiveDTSVersionStatus] = useState<string>("");
    const [requesterContact, setRequesterContact] = useState<any>([]);
    const [reqErrorMessage, setReqErrorMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [descriptionOfChange, setDescriptionOfChange] = useState<string>("");

  const formData = Form.useWatch([], formRef) || {};
  const selectedTransferId = formData.transferId;
  const selectedMSDCompound = formData.MSDCompound;
  const selectedSupplierOrganization = formData.supplierOrganization;
  const selectedMSDCompoundType = formData.MSDCompoundType;

  const MSDCompoundTypeSelection = () => {
    formRef?.setFieldsValue({ MSDCompound: undefined });
  };

  const supplierOrgOptions = useMemo(
    () =>
      Array.isArray(supplierOptionsValue)
        ? supplierOptionsValue.map((item: any) => ({
          label: item,
          value: item,
          key: item,
        }))
        : [],
    [supplierOptionsValue],
  );

  const transferIdOptions = Array.isArray(transferIdList)
    ? transferIdList.map((item: any) => ({
      value: item.transfer_id,
      label: (
        <Tooltip
          title={
            <div>
              <strong>{item.transfer_id}</strong>
              <br />
              {(item.description || "")
                .split("\n")
                .map((line: string, index: number) => (
                  <React.Fragment key={index}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
            </div>
          }
          placement="right"
          overlayInnerStyle={{
            backgroundColor: "white",
            color: "black",
          }}
        >
          <div style={{ width: "100%" }}>{item.transfer_id}</div>
        </Tooltip>
      ),
    }))
    : [];

  const closePopUp = () => {
    setRequesterContact([]);
    setReqErrorMessage(null);
    setIsOfflineDtsModalOpen(false);
  };

  const handleCreateOfflineDTS = async () => {
    try {
      await formRef.validateFields();
      if (!requesterContact || requesterContact.length === 0) {
        setReqErrorMessage("This is a required field.");
        return;
      }
      const fieldsValue = formRef.getFieldsValue();
      setIsSubmitting(true);
      await SaveDraft({
        DTSVersion: fieldsValue?.DTSVersion,
        MSDCompound: fieldsValue?.MSDCompound,
        msd_type: fieldsValue?.MSDCompoundType,
        protocol_number: (fieldsValue?.MSDCompound || "").split("-")[1] || "",
        supplierOrganization: fieldsValue?.supplierOrganization,
        transferId: fieldsValue?.transferId,
        requesters: JSON.stringify(requesterContact || []),
        suppliers: JSON.stringify([]),
        status: "Admin creates draft",
        created_by: getUserEmail() ?? "",
        description_change: descriptionOfChange,
        is_offline_flow: true,
      });
      openNotification("success", "Offline DTS created successfully");
      onCreateSuccess();
      closePopUp();
    } catch (error: any) {
      // validation error: no toast
      if (error?.errorFields?.length) {
        if (!requesterContact || requesterContact.length === 0) {
          setReqErrorMessage("This is a required field.");
        }
        return;
      }
      openNotification(
        "error",
        error?.message || "Failed to save draft",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchAllSupplierOrg = async () => {
      setSupplierLoading(true);
      try {
        const response = await getAllSupplierOrg();
        if ((response as { data?: any })?.data) {
          let msdOrganizations = response?.data as string[];
          if (!msdOrganizations.includes("MSD")) {
            msdOrganizations = ["MSD", ...msdOrganizations];
          }
          const sortedData = msdOrganizations
            .slice()
            .sort((a: string, b: string) => a.localeCompare(b));
          setSupplierOptionsValue(sortedData);
        }
      } finally {
        setSupplierLoading(false);
      }
    };

    const fetchProtocolData = async () => {
      setMsdDataLoading(true);
      try {
        const response = await getProtocolNumber();
        if (response?.data) {
          const sortedData = [...(response.data as ProtocolData[])].sort((a, b) =>
            a.studyName.localeCompare(b.studyName),
          );
          const MSDCompoundOptions = Array.isArray(sortedData)
            ? (sortedData?.map((item: { studyName: any; studyNumber: any }) => ({
              value: item?.studyName,
              studyNumber: item?.studyNumber,
            })) ?? [])
            : [];
          setMSDCompoundODMOptions(MSDCompoundOptions);
        }
      } finally {
        setMsdDataLoading(false);
      }
    };

    const fetchData = async () => {
      try {
        const response = await getDetails();
        if (response?.data) {
          setTransferIdList(response.data as any[]);
        }
      } catch {
        // no-op
      }
    };

    fetchAllSupplierOrg();
    fetchProtocolData();
    fetchData();
  }, [formRef]);

  useEffect(() => {
    if (
      selectedTransferId &&
      selectedMSDCompound &&
      selectedMSDCompound.length > 7 &&
      selectedSupplierOrganization
    ) {
      setDisableDTS(true);
      formRef.setFieldsValue({ DTSVersion: undefined });

      getCurrentDTSVersion(
        selectedMSDCompound,
        selectedSupplierOrganization,
        selectedTransferId,
      )
        .then((response) => {
          const dtsVersion = response?.dts_version
            ? parseInt(response.dts_version, 10)
            : 0;

          setActiveDTSVersion(dtsVersion);
          setActiveDTSVersionStatus(response?.status ?? "");
          setDisableDTS(false);
        })
        .catch(() => {
          setActiveDTSVersion(0);
          setActiveDTSVersionStatus("");
          setDisableDTS(true);
          openNotification("error", "Failed to fetch last active DTS Version");
        });
    } else {
      setDisableDTS(true);
      setActiveDTSVersion(0);
      setActiveDTSVersionStatus("");
      formRef.setFieldsValue({ DTSVersion: undefined });
    }
  }, [
    formRef,
    selectedTransferId,
    selectedMSDCompound,
    selectedSupplierOrganization,
    openNotification,
  ]);

  useEffect(() => {
    const dtsVersion = parseInt(formRef?.getFieldsValue()?.DTSVersion ?? "0", 10);
    if (dtsVersion === 1) {
      setDescriptionOfChange("Original publication");
    } else if (dtsVersion > 1) {
      if (activeDTSVersion === 0) {
        setDescriptionOfChange(descriptionOfChange === "Original publication" || !descriptionOfChange
          ? "First publication created through CDIG"
          : descriptionOfChange);
      } else if (activeDTSVersion > 0) {
        setDescriptionOfChange("");
      }
    }
  }, [formRef?.getFieldsValue()?.DTSVersion, formRef, activeDTSVersion]);

  useEffect(() => {
    if (isModalOpen) {
      formRef.resetFields();
      formRef.setFieldsValue({ MSDCompoundType: "ODM" });
    }
  }, [isModalOpen, formRef]);

  const getFormInputFields = () => {
    return (
      <Form
        form={formRef}
        initialValues={{ remember: true }}
        id="modal-input-form-item"
        wrapperCol={{ style: { maxWidth: "50%" } }}
        layout="vertical"
        style={{ marginTop: "-32px" }}
      >
        <div style={{ marginBottom: reqErrorMessage ? "0px" : "24px" }}>
          <TransferContacts
            onDataChange={(data) => {
              setRequesterContact(data);
              setReqErrorMessage(null);
            }} // No-op function for now
            nextPage="data-transfer"
            contactData={requesterContact}
            errorMessage={reqErrorMessage}
            contactType="requester"
          />
        </div>

        <Form.Item
          className="item-width-50"
          label="Source of MSD Compound and Protocol Number/Identifier"
          name="MSDCompoundType"
          required
          rules={[{ required: true, message: "This is a required field" }]}
        >
          <Radio.Group
            onChange={MSDCompoundTypeSelection}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <Radio value="ODM">Standard source (ODM)</Radio>
            <Radio value="Other">Other source</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          className="item-width-50"
          label="MSD Compound and Protocol Number/Identifier"
          name="MSDCompound"
          required
          rules={[
            { required: true, message: "This is a required field" },
            {
              pattern: /^(MK\d{4}[a-zA-Z]?|MC\d{4}[a-zA-Z]?|V\d{3}[a-zA-Z]?)-[a-zA-Z0-9]{3}$/,
              message:
                'Invalid format. Please use MKXXXX-XXX, MKXXXXX-XXX, MCXXXX-XXX, MCXXXXX-XXX, VXXX-XXX, or VXXXX-XXX (e.g., MK1234-567, MK1234A-12A, MC1234, MC1234A, V123-AAA, V123A-1A2). Please note that if there are 5 characters following a compound with a "MK", "MC" prefix OR 4 letters following a "V" prefix, then the last character in the compound should be an alphabet character, as shown in the examples provided.',
            },
          ]}
        >
          {selectedMSDCompoundType === "ODM" ? (
            <Select
              showSearch
              placeholder="Type to search..."
              optionFilterProp="label"
              suffixIcon={msdDataLoading ? <Spin size="small" /> : null}
              options={MSDCompoundODMOptions?.map((option: any) => ({
                label: option.value,
                value: option.value,
                key: option.studyNumber,
              }))}
            />
          ) : (
            <Input />
          )}
        </Form.Item>

        <Form.Item
          className="item-width-50"
          label="Supplier"
          name="supplierOrganization"
          required
          rules={[{ required: true, message: "This is a required field" }]}
        >
          <Select
            showSearch
            placeholder="Type to search..."
            loading={supplierLoading}
            optionFilterProp="label"
            suffixIcon={supplierLoading ? <Spin size="small" /> : null}
            options={supplierOrgOptions}
          />
        </Form.Item>

        <Form.Item
          className="item-width-50"
          label="Transfer ID"
          name="transferId"
          required
          rules={[{ required: true, message: "This is a required field" }]}
        >
          <Select options={transferIdOptions} />
        </Form.Item>

        <Form.Item
          className="item-width-80"
          label="DTS Version"
          name="DTSVersion"
          required
          tooltip="Enter a DTS version based on your last active version for this compound and transfer ID even if it was prior to using CDIG."
          rules={[
            { required: true, message: "This is a required field" },
            () => ({
              validator: (_, value) =>
                validateDTSVersion(
                  value,
                  activeDTSVersion,
                  activeDTSVersionStatus,
                  false,
                ),
            }),
          ]}
          extra={
            <Form.Item shouldUpdate noStyle>
              {({ getFieldError, getFieldValue }) =>
                DTSTransferMessage(
                  disableDTS,
                  activeDTSVersion,
                  false,
                  activeDTSVersionStatus,
                  getFieldError("DTSVersion")?.length > 0,
                  getFieldValue("DTSVersion"),
                )
              }
            </Form.Item>
          }
        >
          <InputNumber<string>
            min="1"
            step="0.0"
            stringMode
            className="custom-input-number"
            disabled={disableDTS}
          />
        </Form.Item>
      </Form>
    );
  };

  return (
    <>
      <Modal
        width="90vw"
        style={{ maxWidth: "70vw", top: 40 }}
        title={
          <p>
            <span style={{ fontWeight: "bold" }}>Create Offline DTS Agreement</span>
            <br />
            <span style={{ fontSize: "14px", fontWeight: "400" }}>
              Create a transfer for requesters who need to bypass electronic signatures. They&apos;ll be able to download the DTS agreement and sign it offline.
            </span>
          </p>
        }
        open={isModalOpen}
        onCancel={closePopUp}
        footer={[
          <Button key="back" onClick={closePopUp}>
            Cancel
          </Button>,
          <Button
            key="create"
            type="primary"
            onClick={handleCreateOfflineDTS}
            loading={isSubmitting}
          >
            Create
          </Button>,
        ]}
      >
        {getFormInputFields()}
      </Modal>
    </>
  );
};

export default OfflineDtsDailog;
